import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ImageUpload from '../../common/ImageUpload';

const { FiBriefcase, FiPalette, FiType, FiMapPin, FiPhone, FiMail, FiCheck } = FiIcons;

const BusinessSettings = () => {
  const { businessSettings, updateBusinessSettings } = useData();
  const [formData, setFormData] = useState(businessSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setFormData(businessSettings);
  }, [businessSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await updateBusinessSettings(formData);
    setIsLoading(false);
    if (!error) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const colorOptions = ['#3B82F6', '#1E40AF', '#7C3AED', '#DB2777', '#DC2626', '#EA580C', '#D97706', '#65A30D', '#059669', '#0891B2'];
  const fontOptions = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins'];

  return (
    <div className="space-y-10">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter mb-2">Business Settings</h2>
          <p className="text-slate-400 font-medium">Configure your global branding and company details.</p>
        </div>
        {saveSuccess && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-green-500 text-white px-4 py-2 rounded-xl text-xs font-black flex items-center gap-2">
            <SafeIcon icon={FiCheck} /> Settings Saved
          </motion.div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-10">
        <div className="flex flex-col md:flex-row gap-10 items-start">
          <ImageUpload 
            value={formData.logo} 
            onChange={(url) => setFormData({ ...formData, logo: url })} 
            label="Company Logo"
            className="w-32 h-32"
          />
          
          <div className="flex-1 space-y-6 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Company Name</label>
                <input required className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.companyName} onChange={e => setFormData({ ...formData, companyName: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Business Email</label>
                <input type="email" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Business Phone</label>
                <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>
            
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Address</label>
              <textarea rows={2} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-10 border-t border-slate-50">
          <div className="space-y-4">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Brand Colors</h3>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map(color => (
                <button key={color} type="button" onClick={() => setFormData({ ...formData, primaryColor: color })} className={`w-8 h-8 rounded-full border-2 transition-all ${formData.primaryColor === color ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />
              ))}
            </div>
            <input type="color" className="w-full h-10 rounded-xl cursor-pointer" value={formData.primaryColor} onChange={e => setFormData({ ...formData, primaryColor: e.target.value })} />
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Typography</h3>
            <select className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.fontFamily} onChange={e => setFormData({ ...formData, fontFamily: e.target.value })}>
              {fontOptions.map(font => <option key={font} value={font}>{font}</option>)}
            </select>
          </div>
        </div>

        <button type="submit" disabled={isLoading} className="w-full bg-slate-900 text-white py-5 rounded-[2rem] font-black shadow-xl hover:bg-black transition-all disabled:opacity-50">
          {isLoading ? 'Saving Changes...' : 'Update Business Settings'}
        </button>
      </form>
    </div>
  );
};

export default BusinessSettings;